<script setup>
import LegalSummary from 'components/LegalSummary.vue';
import TableOfContents from 'components/TableOfContents.vue';
import { usePageMeta } from 'composables/page-meta';
import { LEGAL_DOCUMENTS_EFFECTIVE_DATE } from 'src/configs/legal';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const tableOfContentsElement = ref();
const tableOfContentsItems = [
    {
        id: 'tldr',
        title: 'TL;DR'
    },
    {
        id: 'definitions',
        title: 'Definitions'
    },
    {
        id: 'requirements',
        title: 'Requirements'
    },
    {
        id: 'proper-use',
        title: 'Proper Use'
    },
    {
        id: 'market-information',
        title: 'Market Information'
    },
    {
        id: 'xprt-jobs-and-job-applications',
        title: 'Xprt Jobs and Job Applications'
    },
    {
        id: 'usage-outside-of-australia',
        title: 'Usage Outside of Australia'
    },
    {
        id: 'intellectual-property',
        title: 'Intellectual Property'
    },
    {
        id: 'account-security',
        title: 'Account Security'
    },
    {
        id: 'career-profiles-and-conduct',
        title: 'Career Profiles and Conduct'
    },
    {
        id: 'custom-profile-aliases',
        title: 'Custom Profile Aliases'
    },
    {
        id: 'links-to-other-websites',
        title: 'Links to Other Websites'
    },
    {
        id: 'termination',
        title: 'Termination'
    },
    {
        id: 'refusal-of-service',
        title: 'Refusal of Service'
    },
    {
        id: 'modifications-to-the-service',
        title: 'Modifications to the Service'
    },
    {
        id: 'discontinuance-to-the-service',
        title: 'Discontinuance of the Service'
    },
    {
        id: 'data-loss',
        title: 'Data Loss'
    },
    {
        id: 'indemnification',
        title: 'Indemnification'
    },
    {
        id: 'representations-and-warranties',
        title: 'Representations and Warranties'
    },
    {
        id: 'limitation-of-liability',
        title: 'Limitation of Liability'
    },
    {
        id: 'unlawful-activities',
        title: 'Unlawful Activities'
    },
    {
        id: 'governing-law-and-dispute-resolution',
        title: 'Governing Law and Dispute Resolution'
    },
    {
        id: 'severability',
        title: 'Severability'
    },
    {
        id: 'changes-to-the-terms-of-service',
        title: 'Changes to the Terms of Service'
    },
    {
        id: 'contact-us',
        title: 'Contact Us'
    }
];

const summaryLead = 'A short summary so you do not have to read all of the terms. The full terms below apply if '
    + 'anything here is unclear.';

const summaryCards = [
    {
        title: 'What Xprt Dev is',
        points: [
            {
                label: 'Market information:',
                text: 'demand, advertised pay, work arrangements and hiring activity for Australian ICT roles, '
                    + 'calculated from Xprt Jobs listings that advertise pay.'
            },
            {
                label: 'A guide, not advice:',
                text: 'the figures describe those listings, not the whole market, and are not career, financial, legal '
                    + 'or tax advice.'
            },
            {
                label: 'An optional account:',
                text: 'save one market in My Market, and build and share a Career profile if you want one.'
            },
            {
                label: 'Part of Xprt Jobs:',
                text: 'one account covers both sites. Job listings and applications live on Xprt Jobs, under its own '
                    + 'terms.'
            }
        ]
    },
    {
        title: 'What we ask of you',
        points: [
            {
                label: 'Be 16 or over:',
                text: 'and give accurate information about yourself.'
            },
            {
                label: 'Use the figures fairly:',
                text: 'quote them with credit and a link, but do not scrape, bulk copy or republish them.'
            },
            {
                label: 'Respect other people:',
                text: 'keep your profile truthful, do not impersonate anyone, and only send invitations and profile '
                    + 'links to people who expect them.'
            },
            {
                label: 'Keep your account secure:',
                text: 'protect your email account and each login service, and tell us if your account is compromised.'
            }
        ]
    },
    {
        title: 'What to expect',
        points: [
            {
                label: 'Figures can change:',
                text: 'they may be delayed, withheld, revised or wrong, so check them before you rely on them.'
            },
            {
                label: 'Your content stays yours:',
                text: 'you own what you add to your Career profile and are responsible for it. We show it only as your '
                    + 'settings allow, and never harvest or sell it.'
            },
            {
                label: 'Nothing to pay:',
                text: 'Xprt Dev has no paid features.'
            },
            {
                label: 'The service can change:',
                text: 'we may change or remove features, and will give at least 30 days\' notice before closing it.'
            },
            {
                label: 'Accounts can close:',
                text: 'you can ask us to close yours at any time, which closes it on both sites, and we may suspend or '
                    + 'close an account that breaks these terms.'
            }
        ]
    },
    {
        title: 'Legal basics',
        points: [
            {
                label: 'Who we are:',
                text: '0x234 Pty Ltd, an Australian company.'
            },
            {
                label: 'As available:',
                text: 'the service comes with no guarantees and limited liability, to the extent the law allows.'
            },
            {
                label: 'Your consumer rights:',
                text: 'nothing in these terms limits the rights the Australian Consumer Law gives you.'
            },
            {
                label: 'Governing law:',
                text: 'South Australia, with any dispute first raised with us in writing.'
            },
            {
                label: 'Questions:',
                text: 'email team@xprt.dev.'
            }
        ]
    }
];

onMounted(() => {
    const anchorId = router.currentRoute.value.hash;
    if (anchorId) {
        setTimeout(() => tableOfContentsElement.value.scrollToAnchor(anchorId.replace('#', '')), 0);
    }
});

usePageMeta(buildPageMeta({
    title: 'Terms of Service',
    description: 'Read the terms for using Xprt Dev market insights, My Market accounts and optional career profiles, '
        + 'including data limitations and links to Xprt Jobs.',
    canonicalUrl: 'https://xprt.dev/legal/terms'
}));
</script>

<template>
    <main class="terms-of-service-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                        Legal · Last updated and effective {{ LEGAL_DOCUMENTS_EFFECTIVE_DATE }}
                    </p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">Terms of Service</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-my-none">
                        These Terms of Service set out your rights and obligations, and ours, when you use Xprt Dev.
                        They are a legal agreement. By using the service, as a guest or with an account, you agree to
                        them.
                    </p>
                </div>
            </div>
        </header>
        <div class="contents-layout container-md q-px-md q-py-xl">
            <aside aria-labelledby="contents-heading" class="contents-rail">
                <p id="contents-heading" class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">On this page</p>
                <nav aria-label="Terms of Service sections">
                    <TableOfContents ref="tableOfContentsElement" :items="tableOfContentsItems"/>
                </nav>
            </aside>
            <article class="legal-copy contents-copy">
                <LegalSummary :cards="summaryCards" :lead="summaryLead"/>
                <a id="definitions" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Definitions</h2>
                <p>
                    For the purposes of these Terms of Service:
                </p>
                <ul>
                    <li>
                        <strong>"We", "our" or "us"</strong> refers to 0x234 Pty Ltd, an Australian proprietary limited company.
                    </li>
                    <li>
                        <strong>"You" or "your"</strong> refers to the person or entity accessing or using the service.
                    </li>
                    <li>
                        <strong>"Service" or "platform"</strong> refers to Xprt Dev, our website at xprt.dev, and the
                        content, application and infrastructure that power it.
                    </li>
                    <li>
                        <strong>"Market information"</strong> refers to the figures, charts and descriptions the service
                        publishes about the Australian ICT job market, such as counts of active roles, advertised
                        salaries and the organisations advertising them.
                    </li>
                    <li>
                        <strong>"Xprt Jobs"</strong> refers to our job search and hiring service at xprt.jobs, which
                        shares its account system with Xprt Dev.
                    </li>
                    <li>
                        <strong>"Account"</strong> refers to a unique account created for you to access the service or parts of the service.
                    </li>
                    <li>
                        <strong>"Career profile"</strong> refers to the optional professional profile you can build,
                        publish and share through the service.
                    </li>
                    <li>
                        <strong>"My Market"</strong> refers to the one market, an ICT specialisation and an Australian
                        location, that an account can save to return to.
                    </li>
                    <li>
                        <strong>"Device"</strong> refers to any electronic device that can access the service, including
                        a computer, a mobile phone or a tablet.
                    </li>
                    <li>
                        <strong>"Personal data"</strong> refers to any information that relates to an identified or identifiable individual.
                    </li>
                    <li>
                        <strong>"Login service"</strong> refers to GitHub, GitLab, Google or LinkedIn, which you can use
                        to log in or to create an account.
                    </li>
                    <li>
                        <strong>"Usage data"</strong> refers to data collected automatically, either generated by the use of the service or from the service infrastructure itself (for
                        example, the duration of a page visit).
                    </li>
                    <li>
                        <strong>"User"</strong> refers to any person or entity accessing the service, with or without an account.
                    </li>
                </ul>
                <a id="requirements" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Requirements</h2>
                <p>
                    You can read the public market pages without an account. An account is what lets you save one market
                    and, if you choose, build a Career profile. To use the service, you agree that:
                </p>
                <ul>
                    <li>You are at least 16 years of age.</li>
                    <li>If you act for an organisation, you have its authority to do so.</li>
                    <li>Any information you give about yourself is current and accurate.</li>
                    <li>You are responsible for all content you provide.</li>
                    <li>
                        You will not use the service to solicit anything that infringes our rights or the rights of
                        others.
                    </li>
                    <li>You will not upload, transmit or otherwise distribute objectionable content.</li>
                </ul>
                <p>
                    If you do not meet these requirements, the service is not for you. We may close an account that does
                    not meet them.
                </p>
                <a id="proper-use" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Proper Use</h2>
                <p>
                    The service is for reading Australian ICT market information, saving one market in My Market, and,
                    if you want one, building and sharing a Career profile. Job search and applications belong to Xprt
                    Jobs.
                </p>
                <p>
                    Use an account for those purposes, and in line with these terms. We may review account activity to
                    check that use. If an account is used for something else, including scraping, spam or impersonation,
                    we may suspend or close it.
                </p>
                <a id="market-information" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Market Information</h2>
                <p>
                    The market information is calculated from job listings on Xprt Jobs that advertise pay. It describes
                    those listings, not every role in the market, and it is not a census of Australian ICT employment.
                    The figures include counts of active roles, advertised salaries, work arrangements, employment
                    types, the organisations advertising them, and, on the overview, movement over 30 days. The
                    <router-link :to="AppRoute.build(Route.Methodology)">methodology</router-link> explains how each
                    figure is calculated and where it stops.
                </p>
                <p>
                    Market information is general information only. It is not career, financial, legal or tax advice,
                    and it does not take your circumstances into account. Consider whether it suits your situation, and
                    seek professional advice where you need it, before you rely on it.
                </p>
                <p>
                    Figures may be delayed, withheld where there are too few listings to report them reliably, revised
                    as listings change, or wrong. We may change how figures are calculated at any time.
                </p>
                <p>
                    An organisation is named because it advertised the listings counted. It may be the employer or a
                    recruitment agency. Naming an organisation is not an endorsement of it, and does not mean it
                    endorses or is affiliated with the service.
                </p>
                <p>
                    You may quote individual figures, for example in an article, a presentation or a salary discussion,
                    provided you credit Xprt Dev and link to the page the figure came from. You must not scrape, crawl
                    or bulk copy the market information, republish it as a dataset, or use it to build a competing
                    product, without our prior written consent.
                </p>
                <a id="xprt-jobs-and-job-applications" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Xprt Jobs and Job Applications</h2>
                <p>
                    Links from the service to Xprt Jobs open job listings on that site, which is governed by the
                    <a href="https://xprt.jobs/legal/terms" target="_blank">Xprt Jobs Terms of Service</a>. Job listings
                    are written by the employers and recruiters who advertise the roles, and we do not guarantee that
                    any listing is accurate, current or still open.
                </p>
                <p>
                    Xprt Dev and Xprt Jobs share one account system. If you apply for a role that accepts applications
                    on Xprt Jobs, basic details from your Career profile may prefill the application, and what you
                    submit is sent to the employer. The employer receives that application, not your Career profile. An
                    application you make on an employer's or recruiter's own site is separate, and Xprt Dev does not
                    handle it. Your dealings with an employer, including any offer of employment, are between you and
                    that employer.
                </p>
                <a id="usage-outside-of-australia" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Usage Outside of Australia</h2>
                <p>
                    The service is focused on the Australian ICT job market, and its market information covers
                    Australian job listings only. You are welcome to use it from anywhere, but our operations are based
                    in Australia, and we are subject to Australian laws and regulations.
                </p>
                <p>
                    If you access the service from outside Australia, you do so on your own initiative. You are
                    responsible for complying with the laws of the place you access it from, including laws on data
                    privacy, content and online conduct.
                </p>
                <p>
                    Please be aware that the information you provide or the content you share on the service is stored
                    and processed primarily in Singapore. Certain service components operate from Australia, including
                    secure backup storage and storage for public files such as brand logos and Career profile photos,
                    and some static assets may be cached and served from globally distributed edge locations. By using
                    the service, you consent to such transfer, storage, and processing in accordance with applicable
                    laws, which may differ from those in your country.
                </p>
                <a id="intellectual-property" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Intellectual Property</h2>
                <p>
                    You own the content you add to the service, such as your Career profile, and you are responsible for
                    it. Only add content you have the right to share. We do not claim ownership of your content, and we
                    do not harvest it or sell it.
                </p>
                <p>
                    So that we can run the service, you give us a free, non-exclusive permission to store, copy and
                    display your content, only as your settings allow. That includes the copies our hosting and delivery
                    network keep so that pages load quickly. The permission ends when you remove the content or your
                    account is closed. An application you submit on Xprt Jobs is outside this permission and follows the
                    <a href="https://xprt.jobs/legal/terms" target="_blank">Xprt Jobs Terms of Service</a>.
                </p>
                <p>
                    Apart from the content you provide, the service, including the market information, its design and
                    its software, belongs to us or our licensors. Organisation names and logos belong to their
                    respective owners.
                </p>
                <p>
                    You may not copy, modify, reverse engineer, disassemble, decompile, remove, reproduce, resell, mirror or create derivative works or updates of, or attempt to derive
                    the source code of the service, except with prior written consent.
                </p>
                <p>
                    Violations of the obligations above, as well as the attempt of such infringement may be subject to immediate account termination and/or legal consequences.
                </p>
                <a id="account-security" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Account Security</h2>
                <p>
                    When you log in by email, we send a one-time code to your email address. You are responsible for
                    keeping your email account secure, and you must not share a one-time code with anyone.
                </p>
                <p>
                    If you log in with a login service, you are responsible for keeping that service, and your
                    credentials for it, secure. Two-factor authentication is not required. We recommend it for your
                    email account and for each login service. If your account is compromised, or someone else is using
                    it, tell us immediately.
                </p>
                <p>
                    The service does not store access tokens, and it does not keep ongoing access to a login service
                    after you log in. A token is used only to log you in, and is then discarded.
                </p>
                <p>
                    Logging out of a login service does not log you out of Xprt Dev. On a device that is not yours
                    alone, including a public or work device, log out of Xprt Dev when you finish.
                </p>
                <a id="career-profiles-and-conduct" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Career Profiles and Conduct</h2>
                <p>
                    A Career profile is optional. If you create one, you are responsible for everything in it and for
                    who you share it with. You agree that:
                </p>
                <ul>
                    <li>
                        Your profile is accurate, and does not misrepresent your experience, qualifications or
                        employment history.
                    </li>
                    <li>
                        You will not impersonate another person or organisation, or suggest an affiliation you do not
                        have.
                    </li>
                    <li>
                        You will not publish another person's personal information without their permission, or
                        information you are not entitled to share, such as the confidential information of a current or
                        former employer.
                    </li>
                    <li>
                        You will only send invitations and profile links to people who would expect to hear from you,
                        and will not use them for spam or unsolicited promotion.
                    </li>
                </ul>
                <p>
                    Linking a GitHub or GitLab account shows your public contribution activity from that service. It
                    does not verify your skills, and we do not verify the accuracy of any profile.
                </p>
                <p>
                    You can report a profile you believe breaks these terms. We may hide or remove content, restrict
                    features, or suspend or close an account that breaks these terms.
                </p>
                <a id="custom-profile-aliases" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Custom Profile Aliases</h2>
                <p>
                    You can choose the alias that forms your profile address, such as
                    <strong>xprt.dev/p/youralias</strong>. An alias must follow these rules:
                </p>
                <ul>
                    <li>
                        <strong>Prohibited language:</strong> Do not use racist, derogatory, abusive or obscene words,
                        or any other language we are not prepared to publish.
                    </li>
                    <li>
                        <strong>Other people's rights:</strong> Do not use a name or phrase that infringes someone
                        else's intellectual property, or that suggests you are a person or organisation you are not.
                    </li>
                </ul>
                <p>
                    We may change an alias, or require you to change it, when it breaks these rules. Changing it makes
                    the previous address stop working.
                </p>
                <a id="links-to-other-websites" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Links to Other Websites</h2>
                <p>
                    Market pages link to Xprt Jobs, which we operate. Its own terms and privacy policy apply once you
                    are there, as described above.
                </p>
                <p>
                    A Career profile may also link to websites we do not operate, such as a personal site or another
                    public profile. If you follow one of those links, that site's own terms and privacy policy apply. We
                    do not control those sites, and we are not responsible for their content or for what you do there.
                    We will make a reasonable effort to remove or block a harmful link a member has added, once we know
                    about it. We cannot promise we will find every such link first.
                </p>
                <p>
                    Read the privacy policy and terms of any other site before you share personal information there.
                </p>
                <a id="termination" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Termination</h2>
                <p>
                    You may end this agreement at any time by asking us to close your account and by stopping all use of
                    the service. Email <a href="mailto:team@xprt.dev" target="_blank">team@xprt.dev</a> or
                    <a href="mailto:team@xprt.jobs" target="_blank">team@xprt.jobs</a> from the address registered to
                    the account. A request to either address closes the account on both sites. We may ask for further
                    identification before we close it, so that we do not act on a request from someone else. There is no
                    control on Xprt Dev or Xprt Jobs that closes the account for you. Closure deletes the account and
                    the data held for it, including your Career profile and My Market. A job application is the
                    exception. Its record, and the resume and cover letter attached to it, stay for the period in the
                    <a href="https://xprt.jobs/legal/privacy" target="_blank">Xprt Jobs Privacy Policy</a>. Closing the
                    account does not shorten that period, and it does not recall a copy an advertiser has already
                    downloaded.
                </p>
                <p>
                    We may suspend or close an account that breaks these terms.
                </p>
                <a id="refusal-of-service" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Refusal of Service</h2>
                <p>
                    At our discretion, we reserve the right to refuse service to any user, to terminate any accounts owned by the user, and if applicable, to disallow any devices
                    associated with the user.
                </p>
                <a id="modifications-to-the-service" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Modifications to the Service</h2>
                <p>
                    We continually strive to improve our service and may add, modify or remove features or functionality to the service at our discretion. In case of removal or
                    modification of certain features, those changes may affect your ability to use those features or the service in the same way as prior to the change. We will attempt
                    to notify you of any significant changes to features that could affect your use of the service, but we are under no obligation to do so.
                </p>
                <p>
                    By continuing to use the service after those changes become effective, you agree to be bound by the
                    revised service.
                </p>
                <a id="discontinuance-to-the-service" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Discontinuance of the Service</h2>
                <p>
                    At our discretion, we reserve the right to discontinue the service and cease further operation with no less than 30 days prior notice. This notice will be provided
                    on the service.
                </p>
                <a id="data-loss" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Data Loss</h2>
                <p>
                    We will not be responsible under any circumstances for any data that is lost, corrupted or rendered unreadable due to:
                </p>
                <ul>
                    <li>Communication and/or transmission errors, or other related failures.</li>
                    <li>Equipment failures (including but not limited to silent hardware corruption-related issues).</li>
                    <li>Our failure to backup or secure data, or the loss or damage to backup media or facilities.</li>
                    <li>
                        Data kept only in your browser, such as your theme or a dismissed notice, that you or your
                        browser then cleared.
                    </li>
                </ul>
                <a id="indemnification" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Indemnification</h2>
                <p>
                    You agree to indemnify and hold us harmless from any and all demands, loss, liability, claims or expenses (including legal fees) made against us by any third-party
                    due to or arising out of or in connection with your use of the service, including but not limited to:
                </p>
                <ul>
                    <li>A breach of any obligation, representation or warranty hereunder.</li>
                    <li>Misuse of any feature of the service, or of a system related to it.</li>
                    <li>Infringement of anyone's intellectual property or proprietary or personal rights.</li>
                </ul>
                <p>
                    You acknowledge and agree that it is your sole responsibility to accurately utilise the platform, adhere to these Terms of Service, respect other users' rights,
                    and comply with all applicable laws. We shall not be held liable for any consequences, financial or otherwise, arising from misuse of the service, or
                    disputes that may occur with other users or third parties.
                </p>
                <a id="representations-and-warranties" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Representations and Warranties</h2>
                <p>
                    The service is provided to you on an "as is" and "as available" basis. To the extent the law allows,
                    we do not provide warranties of any kind, whether express or implied, including warranties of
                    merchantability and fitness for a particular purpose, or any warranty that:
                </p>
                <ul>
                    <li>The service will meet your specific requirements.</li>
                    <li>The service will be uninterrupted, timely, secure or error-free.</li>
                    <li>The results that may be obtained from the use of the service will be accurate or reliable.</li>
                    <li>Market information, or a job listing opened on Xprt Jobs, will meet your expectations.</li>
                    <li>Any errors in the service will be corrected.</li>
                    <li>Any requested enhancements will be delivered.</li>
                </ul>
                <a id="limitation-of-liability" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Limitation of Liability</h2>
                <p>
                    To the maximum extent permitted by law, you assume full responsibility for, and we disclaim liability to you for any indirect, consequential, exemplary, incidental
                    or punitive damages, including lost profits, even if we had been advised of the possibility of such damages.
                </p>
                <p>
                    Nothing in these Terms of Service excludes, restricts or modifies any right or remedy you have under
                    the Australian Consumer Law, or any other law, that cannot lawfully be excluded, restricted or
                    modified.
                </p>
                <a id="unlawful-activities" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Unlawful Activities</h2>
                <p>
                    We do not condone, under any circumstances, the use of the service for unlawful activities, harassment of other users, or the distribution of harmful or
                    inappropriate content. You agree not to use the service for any such purpose.
                </p>
                <p>
                    Any activities suspected of being unlawful, harassing, harmful or inappropriate will result in the immediate suspension or termination of service, at our
                    discretion. These actions may also lead to legal consequences. We reserve the right to report any suspected unlawful activities to law enforcement authorities.
                </p>
                <a id="governing-law-and-dispute-resolution" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Governing Law and Dispute Resolution</h2>
                <p>
                    You agree that all matters relating to your access to or use of the service, including all disputes, will be governed by the law of South Australia, Australia, and
                    the parties submit to the non-exclusive jurisdiction of the courts exercising jurisdiction there.
                </p>
                <p>
                    You must not commence arbitration or court proceedings about a dispute unless you have followed this
                    clause, except where you seek urgent injunctive relief. If a dispute arises out of or in relation to
                    these terms, the following steps apply:
                </p>
                <ul>
                    <li>
                        The party raising the dispute must first notify the other party in writing of the dispute, and provide sufficient detail to enable the dispute to be considered.
                    </li>
                    <li>
                        Both parties must discuss or enter into correspondence about the dispute and attempt to resolve it.
                    </li>
                    <li>
                        If the dispute is not resolved within 30 days of when the dispute was first notified, then the dispute shall be submitted to arbitration in accordance with, and
                        subject to the Institute of Arbitrators and Mediators Australia Fast Track Arbitration Rules.
                    </li>
                </ul>
                <a id="severability" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Severability</h2>
                <p>
                    In the event that any provision of these Terms of Service is found to be unlawful, conflicting with another provision of the Terms, or otherwise unenforceable, the
                    agreement will remain in force as though it had been entered into without that unenforceable provision being included in it. If two or more provisions are deemed to
                    conflict with each other's operation, we shall have the sole right to elect which provision remains in force.
                </p>
                <a id="changes-to-the-terms-of-service" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Changes to the Terms of Service</h2>
                <p>
                    You acknowledge that, from time to time, circumstances may arise that make it necessary or desirable to vary certain provisions of these terms, in which we reserve
                    the right to do so at any time. Unless explicitly stated, any new features that modify or enhance the service shall be subject to these terms.
                </p>
                <p>
                    Changes take effect on the date shown at the top of these Terms of Service. We will let you know of
                    a material change via a prominent notice on the service before it takes effect.
                </p>
                <p>
                    You are advised to review these Terms of Service periodically for any changes, and any continued use of the service following the posting of changes constitutes
                    your acceptance of the updated Terms of Service.
                </p>
                <a id="contact-us" class="no-hover-decoration"></a>
                <h2 class="text-h4 text-weight-bold">Contact Us</h2>
                <p>
                    If you have a question about these terms, email
                    <a href="mailto:team@xprt.dev" target="_blank">team@xprt.dev</a>.
                </p>
                <p>
                    We aim to answer promptly. For an urgent question about these terms, put "Urgent: Terms of Service
                    Inquiry" in the subject line.
                </p>
            </article>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.terms-of-service-page {
    .hero-copy {
        max-width: 880px;
    }

    .hero-deck {
        max-width: 720px;
    }
}
</style>
